import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Header } from '../components';
import { 
  FiArrowLeft, FiVideo, FiClock, FiCalendar, FiPlay, 
  FiUsers, FiUser, FiBookOpen, FiDownload, FiExternalLink, FiPlus, FiRefreshCw
} from 'react-icons/fi';
import { useStateContext } from '../contexts/ContextProvider';
import axios from 'axios';

const BatchLive = () => {
  const { batchId } = useParams();
  const navigate = useNavigate();
  const { currentColor } = useStateContext();
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [batch, setBatch] = useState(null);
  const [mergedClasses, setMergedClasses] = useState([]);
  const [teacherMap, setTeacherMap] = useState({});
  const [creatingClass, setCreatingClass] = useState(false);
  const [syncingRecordings, setSyncingRecordings] = useState(false);
  
  // Success modal states
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [joinUrl, setJoinUrl] = useState('');
  const [createdClassName, setCreatedClassName] = useState('');
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const ITEMS_PER_PAGE = 25;
  
  // Get teacher info from localStorage
  const { teacherName, teacherId } = useMemo(() => {
    const storedName = localStorage.getItem('name');
    const storedId = localStorage.getItem('user_id');
    return {
      teacherName: storedName || 'Teacher',
      teacherId: storedId ? parseInt(storedId, 10) : null
    };
  }, []);
  
  // New class form state - simplified
  const [showNewClassForm, setShowNewClassForm] = useState(false);
  const [newClass, setNewClass] = useState({
    title: '',
    start_time: '',
    subject: ''
  });
  
  const API_BASE_URL = 'https://apissc.sainikschoolcadet.com/api';

  // Fetch teacher info
  const fetchTeacherInfo = useCallback(async (teacherIdToFetch) => {
    if (!teacherIdToFetch || teacherMap[teacherIdToFetch]) {
      return teacherMap[teacherIdToFetch] || null;
    }
    
    try {
      const response = await axios.get(`${API_BASE_URL}/Users/getbyid/${teacherIdToFetch}`);
      
      if (response.data?.f_name) {
        const teacherName = response.data.f_name;
        setTeacherMap(prev => ({
          ...prev,
          [teacherIdToFetch]: teacherName
        }));
        return teacherName;
      }
      
      return null;
    } catch (err) {
      console.error(`Error fetching teacher info for ID ${teacherIdToFetch}:`, err);
      return null;
    }
  }, [teacherMap]);

  // Batch fetch all teacher info
  const fetchAllTeachersInfo = useCallback(async (teacherIds) => {
    const uniqueIds = [...new Set(teacherIds.filter(id => id && !teacherMap[id]))];
    
    if (uniqueIds.length === 0) return;

    try {
      const teacherPromises = uniqueIds.map(async (id) => {
        try {
          const response = await axios.get(`${API_BASE_URL}/Users/getbyid/${id}`);
          return {
            id,
            name: response.data?.f_name || `Teacher ${id}`
          };
        } catch (err) {
          console.error(`Error fetching teacher ${id}:`, err);
          return {
            id,
            name: `Teacher ${id}`
          };
        }
      });

      const teacherResults = await Promise.all(teacherPromises);
      
      setTeacherMap(prev => {
        const newMap = { ...prev };
        teacherResults.forEach(({ id, name }) => {
          newMap[id] = name;
        });
        return newMap;
      });
    } catch (err) {
      console.error('Error fetching teachers info:', err);
    }
  }, [teacherMap]);
  
  // Load data
  const fetchData = useCallback(async () => {
    if (!batchId) {
      setError('Batch ID is required');
      setLoading(false);
      return;
    }
    
    try {
      setLoading(true);
      setError(null);
      
      // Fetch classes and recordings
      const [classesResponse, recordingsResponse] = await Promise.all([
        axios.get(`${API_BASE_URL}/LiveClasses/batch/${batchId}`),
        axios.get(`${API_BASE_URL}/LiveClasses/batch-recordings/${batchId}`)
      ]);
      
      setBatch(classesResponse.data.batch);
      
      const classes = classesResponse.data.liveClasses || [];
      const recordings = recordingsResponse.data.recordings || [];
      
      // Get teacher info
      const teacherIds = [...new Set(classes.map(cls => cls.teacher_id))];
      await fetchAllTeachersInfo(teacherIds);
      
      // Merge classes with recordings
      const merged = classes.map(liveClass => {
        const classRecording = recordings.find(rec => rec.liveClassId === liveClass.live_class_id);
        const recording = classRecording?.recordings?.[0] || null;
        
        return {
          ...liveClass,
          recording
        };
      });
      
      // Sort by start time (newest first)
      const sortedMerged = merged.sort((a, b) => new Date(b.start_time) - new Date(a.start_time));
      setMergedClasses(sortedMerged);
      
    } catch (err) {
      console.error('Error fetching data:', err);
      setError('Failed to load live classes data');
    } finally {
      setLoading(false);
    }
  }, [batchId, fetchAllTeachersInfo]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);
  
  // Navigation handler
  const handleGoBack = useCallback(() => {
    navigate(`/BatchView/${batchId}`);
  }, [navigate, batchId]);

  // Sync all recordings for the batch
  const syncAllRecordings = useCallback(async () => {
    try {
      setSyncingRecordings(true);
      setError(null);
      
      const classesWithoutRecordings = mergedClasses.filter(cls => !cls.recording && cls.status !== 'live');
      
      if (classesWithoutRecordings.length === 0) {
        alert('No classes need recording sync.');
        return;
      }
      
      let totalFound = 0;
      let totalSaved = 0;
      
      // Sync recordings for each class that doesn't have recordings
      for (const classData of classesWithoutRecordings) {
        try {
          const response = await axios.post(`${API_BASE_URL}/LiveClasses/sync-recordings/${classData.live_class_id}`);
          if (response.data) {
            totalFound += response.data.recordingsFound || 0;
            totalSaved += response.data.newRecordingsSaved || 0;
          }
        } catch (err) {
          console.error(`Error syncing recordings for class ${classData.live_class_id}:`, err);
        }
      }
      
      const message = totalSaved > 0
        ? `Sync complete! Found ${totalFound} recordings across all classes, saved ${totalSaved} new ones.`
        : `Sync complete! Found ${totalFound} recordings (all already synced).`;
      
      alert(message);
      
      // Refresh the data
      await fetchData();
      
    } catch (err) {
      console.error('Error syncing all recordings:', err);
      setError('Failed to sync recordings');
    } finally {
      setSyncingRecordings(false);
    }
  }, [mergedClasses, fetchData]);
  
  // Create live class with success modal
  const createLiveClass = useCallback(async (e) => {
    e.preventDefault();
    
    try {
      setCreatingClass(true);
      setError(null);

      // Log current time and timezone info for debugging
      const now = new Date();
      console.log('=== DEBUG: Class Creation Started ===');
      console.log('Current local time:', now.toString());
      console.log('Current UTC time:', now.toISOString());
      console.log('Timezone offset (minutes):', now.getTimezoneOffset());
      console.log('User timezone:', Intl.DateTimeFormat().resolvedOptions().timeZone);

      // Prepare data for API (let backend handle all validation)
      const classData = {
        title: newClass.title,
        batch_id: parseInt(batchId),
        teacher_id: parseInt(teacherId),
        start_time: newClass.start_time,
        subject: newClass.subject
      };

      console.log('Sending class data:', classData);
      
      // Log the start_time in different formats to debug timezone issues
      if (newClass.start_time) {
        const startTimeDate = new Date(newClass.start_time);
        console.log('Start time analysis:', {
          original: newClass.start_time,
          asDate: startTimeDate.toString(),
          asISOString: startTimeDate.toISOString(),
          asUTC: startTimeDate.toUTCString(),
          timestamp: startTimeDate.getTime(),
          isInFuture: startTimeDate > now,
          timeDifferenceMinutes: (startTimeDate.getTime() - now.getTime()) / (1000 * 60)
        });
      }

      // Send to API
      const response = await axios.post(`${API_BASE_URL}/LiveClasses`, classData, {
        headers: {
          'Content-Type': 'application/json',
        }
      });

      console.log('=== FULL API RESPONSE ===');
      console.log('Status:', response.status);
      console.log('Headers:', response.headers);
      console.log('Complete response data:', JSON.stringify(response.data, null, 2));

      // Log specific parts of the response
      if (response.data) {
        console.log('=== RESPONSE BREAKDOWN ===');
        console.log('Message:', response.data.message);
        
        if (response.data.liveClass) {
          console.log('Live Class Data:', {
            id: response.data.liveClass.live_class_id,
            title: response.data.liveClass.title,
            start_time: response.data.liveClass.start_time,
            created_at: response.data.liveClass.created_at,
            meeting_id: response.data.liveClass.meeting_id
          });
          
          // Check if start_time is in the past
          const apiStartTime = new Date(response.data.liveClass.start_time);
          console.log('API Start Time Analysis:', {
            apiStartTime: apiStartTime.toString(),
            apiStartTimeISO: apiStartTime.toISOString(),
            currentTime: now.toISOString(),
            isStartTimeInPast: apiStartTime < now,
            timeDifference: (apiStartTime.getTime() - now.getTime()) / (1000 * 60) + ' minutes'
          });
        }
        
        if (response.data.bbbMeeting) {
          console.log('BBB Meeting Data:', {
            meetingId: response.data.bbbMeeting.meetingId,
            joinUrl: response.data.bbbMeeting.joinUrl,
            recording: response.data.bbbMeeting.recording,
            duration: response.data.bbbMeeting.duration,
            scheduledStartTime: response.data.bbbMeeting.scheduledStartTime
          });
          
          if (response.data.bbbMeeting.scheduledStartTime) {
            const bbbStartTime = new Date(response.data.bbbMeeting.scheduledStartTime);
            console.log('BBB Start Time Analysis:', {
              bbbStartTime: bbbStartTime.toString(),
              bbbStartTimeISO: bbbStartTime.toISOString(),
              currentTime: now.toISOString(),
              isBBBStartTimeInPast: bbbStartTime < now,
              timeDifference: (bbbStartTime.getTime() - now.getTime()) / (1000 * 60) + ' minutes'
            });
          }
        }
        
        if (response.data.notification) {
          console.log('Notification:', response.data.notification);
        }
      }

      // Check if successful
      if (response.data && response.data.bbbMeeting && response.data.bbbMeeting.joinUrl) {
        const joinUrl = response.data.bbbMeeting.joinUrl;
        
        console.log('=== CLASS CREATED SUCCESSFULLY ===');
        console.log('Join URL:', joinUrl);

        // Store data for success modal
        setJoinUrl(joinUrl);
        setCreatedClassName(newClass.title);

        // Reset form
        setNewClass({
          title: '',
          start_time: '',
          subject: ''
        });

        setShowNewClassForm(false);
        setShowSuccessModal(true);
        
      } else {
        console.error('❌ Invalid response structure');
        console.error('Available response data:', response.data);
        throw new Error('Invalid response from server');
      }
      
    } catch (err) {
      console.error('=== ERROR CREATING LIVE CLASS ===');
      console.error('Error object:', err);
      console.error('Error message:', err.message);
      
      if (err.response) {
        console.error('Error response status:', err.response.status);
        console.error('Error response data:', err.response.data);
        console.error('Error response headers:', err.response.headers);
      }
      
      if (err.request) {
        console.error('Error request:', err.request);
      }
      
      // Show the actual error message from API
      let errorMessage = 'Failed to create class';
      if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      setError(errorMessage);
    } finally {
      setCreatingClass(false);
      console.log('=== Class creation process finished ===');
    }
  }, [newClass, batchId, teacherId]);
  
  // Success Modal Component
  const SuccessModal = () => {
    if (!showSuccessModal) return null;

    const handleJoinClass = () => {
      window.open(joinUrl, '_blank');
      setShowSuccessModal(false);
      // Refresh the data to show the new class
      setTimeout(() => {
        fetchData();
      }, 1000);
    };

    const handleClose = () => {
      setShowSuccessModal(false);
      // Refresh the data to show the new class
      setTimeout(() => {
        fetchData();
      }, 1000);
    };

    return (
      <div className="fixed inset-0 z-50 overflow-y-auto">
        <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75" onClick={handleClose}></div>
          
          <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
            <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
              <div className="sm:flex sm:items-start">
                <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-green-100 sm:mx-0 sm:h-10 sm:w-10">
                  <svg className="h-6 w-6 text-green-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                  <h3 className="text-lg leading-6 font-medium text-gray-900">
                    Class Created Successfully! 🎉
                  </h3>
                  <div className="mt-2">
                    <p className="text-sm text-gray-500">
                      "{createdClassName}" has been created and is ready to start. You can join the meeting room now or close this dialog and join later from the class list.
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
              <button
                type="button"
                className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 text-base font-medium text-white sm:ml-3 sm:w-auto sm:text-sm"
                style={{ backgroundColor: currentColor }}
                onClick={handleJoinClass}
              >
                🚀 Join Class Now
              </button>
              <button
                type="button"
                className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                onClick={handleClose}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };
  
  // Join as teacher
  const joinAsTeacher = useCallback(async (classId) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/LiveClasses/join-teacher/${classId}`, {
        params: { 
          username: teacherName,
          teacherId: teacherId,
          verify: 'true'
        }
      });
      
      if (response.data?.joinUrl) {
        window.open(response.data.joinUrl, '_blank');
      } else {
        alert('Unable to join class - no meeting URL received');
      }
    } catch (error) {
      console.error('Error joining class:', error);
      alert('Failed to join class: ' + (error.response?.data?.message || error.message));
    }
  }, [teacherName, teacherId]);
  
  // Form change handler
  const handleFormChange = useCallback((e) => {
    const { name, value } = e.target;
    setNewClass(prev => ({
      ...prev,
      [name]: value
    }));
  }, []);
  
  // Date formatting
  const formatDate = useCallback((dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: '2-digit',
      timeZone: 'UTC',
    });
  }, []);
  
  const formatTime = useCallback((dateString) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
      timeZone: 'UTC',
    });
  }, []);
  
  // Check if class is live now
  const isLiveNow = useCallback((startTime) => {
    if (!startTime) return false;
    
    const start = new Date(startTime);
    const end = new Date(start.getTime() + 60 * 60000); // 60 minutes duration
    const now = new Date();
    
    return now >= start && now <= end;
  }, []);

  // Paginated data
  const paginatedData = useMemo(() => {
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    const endIndex = startIndex + ITEMS_PER_PAGE;
    return mergedClasses.slice(startIndex, endIndex);
  }, [mergedClasses, currentPage]);

  const totalPages = Math.ceil(mergedClasses.length / ITEMS_PER_PAGE);

  // Table rows
  const tableRows = useMemo(() => {
    return paginatedData.map((classData, index) => {
      const globalIndex = (currentPage - 1) * ITEMS_PER_PAGE + index + 1;
      
      return (
        <tr key={classData.live_class_id} className="hover:bg-gray-50 transition-colors duration-150">
          <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500 font-medium">
            {globalIndex}
          </td>
          <td className="px-4 py-4 whitespace-nowrap">
            <div className="flex items-center">
              <div className={`flex-shrink-0 h-8 w-8 rounded-full flex items-center justify-center ${
                classData.status === 'live' ? 'bg-green-100 text-green-600' : 'bg-gray-100 text-gray-500'
              }`}>
                <FiVideo size={14} />
              </div>
              <div className="ml-3">
                <div className="text-sm font-medium text-gray-900">{classData.title}</div>
                <div className="text-xs text-gray-500">{classData.meeting_name}</div>
              </div>
            </div>
          </td>
          <td className="px-4 py-4 whitespace-nowrap">
            <div className="text-sm text-gray-800 font-medium">{formatDate(classData.start_time)}</div>
            <div className="text-xs text-gray-500">{formatTime(classData.start_time)}</div>
          </td>
          <td className="px-4 py-4 whitespace-nowrap">
            <div className="text-sm text-gray-700 flex items-center">
              <FiBookOpen className="text-gray-400 mr-1.5" size={14} />
              <span>{classData.subject || 'Not specified'}</span>
            </div>
          </td>
          <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-700">
            <div className="flex items-center">
              <FiUser className="text-gray-400 mr-1.5" size={14} />
              <span>{teacherMap[classData.teacher_id] || `ID: ${classData.teacher_id}`}</span>
            </div>
          </td>
          <td className="px-4 py-4 whitespace-nowrap text-center">
            <span className={`inline-flex items-center justify-center px-3 py-1 rounded-full text-xs font-medium ${
              classData.status === 'live'
                ? 'bg-green-100 text-green-800'
                : classData.recording ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-800'
            }`}>
              {classData.status === 'live' ? (
                <>
                  <span className="w-1.5 h-1.5 rounded-full bg-green-500 mr-1.5 animate-pulse"></span> Live
                </>
              ) : classData.recording ? 'RECORDED' : 'ENDED'}
            </span>
          </td>
          <td className="px-4 py-4 whitespace-nowrap text-center">
            {classData.status === 'live' ? (
              <button
                onClick={() => joinAsTeacher(classData.live_class_id)}
                className="px-3 py-1.5 rounded-md text-white text-xs font-medium inline-flex items-center gap-1 shadow-sm"
                style={{ backgroundColor: currentColor }}
              >
                <FiPlay size={12} /> Join Live
              </button>
            ) : classData.recording ? (
              <button 
                className="bg-blue-50 border border-blue-200 text-blue-700 hover:bg-blue-100 transition-colors duration-150 px-3 py-1.5 rounded-md text-xs font-medium inline-flex items-center gap-1 shadow-sm"
                onClick={() => window.open(classData.recording.url, '_blank')}
              >
                <FiPlay size={12} /> Watch Recording
              </button>
            ) : (
              <span className="text-gray-400 text-xs italic">No Recording</span>
            )}
          </td>
        </tr>
      );
    });
  }, [paginatedData, teacherMap, formatDate, formatTime, joinAsTeacher, currentColor, currentPage]);
  
  // Loading state
  if (loading) {
    return (
      <div className="m-2 md:m-10 mt-24 p-2 md:p-10 bg-white rounded-3xl">
        <Header category=" " title="Loading..." />
        <div className="flex justify-center items-center h-64">
          <div className="animate-pulse flex flex-col items-center">
            <div className="w-12 h-12 rounded-full bg-gray-200 mb-3"></div>
            <p className="text-xl text-gray-600">Loading class data...</p>
          </div>
        </div>
      </div>
    );
  }
  
  // Error state
  if (error && !showNewClassForm) {
    return (
      <div className="m-2 md:m-10 mt-24 p-2 md:p-10 bg-white rounded-3xl">
        <Header category=" " title="Error" />
        <div className="mt-6 max-w-2xl mx-auto">
          <div className="bg-red-50 border-l-4 border-red-400 p-6 rounded-r-lg shadow-sm">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-8 w-8 text-red-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-4 flex-1">
                <h3 className="text-lg font-medium text-red-800 mb-2">Error</h3>
                <p className="text-red-700 mb-4">{error}</p>
                <div className="flex gap-3">
                  <button 
                    onClick={() => window.location.reload()}
                    className="px-4 py-2 bg-red-600 text-white rounded-md text-sm font-medium hover:bg-red-700"
                  >
                    Try Again
                  </button>
                  <button 
                    onClick={handleGoBack}
                    className="px-4 py-2 bg-white border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                  >
                    <FiArrowLeft className="inline mr-1" /> Back to Batch
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
  
  const classesWithoutRecordings = mergedClasses.filter(cls => !cls.recording && cls.status !== 'live').length;
  
  return (
    <div className="m-2 md:m-10 mt-24 p-2 md:p-10 bg-white rounded-3xl">
      {/* Header */}
      <div className="mb-6">
        <button 
          onClick={handleGoBack}
          className="mb-4 flex items-center text-gray-600 hover:text-gray-900"
        >
          <FiArrowLeft className="mr-2" /> Back to Batch Details
        </button>
        
        <div className="flex flex-col md:flex-row md:items-center md:justify-between">
          <div>
            <Header 
              category=" " 
              title={(batch?.name) ? `${batch.name} - Live Classes` : 'Live Classes'} 
            />
          </div>
        </div>
      </div>
      
      {/* Action Buttons */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
        <div>
          <h3 className="text-lg font-medium">Class History & Recordings</h3>
          {mergedClasses.length > 0 && (
            <p className="text-sm text-gray-500 mt-1">
              Showing {Math.min((currentPage - 1) * ITEMS_PER_PAGE + 1, mergedClasses.length)} - {Math.min(currentPage * ITEMS_PER_PAGE, mergedClasses.length)} of {mergedClasses.length} classes
            </p>
          )}
        </div>
        <div className="flex gap-3">
          {classesWithoutRecordings > 0 && (
            <button
              onClick={syncAllRecordings}
              disabled={syncingRecordings}
              className="px-4 py-2 bg-orange-50 border border-orange-200 text-orange-700 hover:bg-orange-100 transition-colors duration-150 rounded-md text-sm font-medium inline-flex items-center gap-2 shadow-sm disabled:opacity-50"
            >
              {syncingRecordings ? (
                <>
                  <FiRefreshCw size={14} className="animate-spin" /> Syncing All...
                </>
              ) : (
                <>
                  <FiRefreshCw size={14} /> Sync All Recordings ({classesWithoutRecordings})
                </>
              )}
            </button>
          )}
          <button
            onClick={() => setShowNewClassForm(true)}
            className="px-4 py-2 rounded-md text-white text-sm font-medium inline-flex items-center gap-2 shadow-sm"
            style={{ backgroundColor: currentColor }}
          >
            <FiPlus size={14} /> Create New Class
          </button>
        </div>
      </div>
      
      {/* Classes Table */}
      <div>
        {mergedClasses.length > 0 ? (
          <>
            <div className="overflow-x-auto rounded-lg border border-gray-200 shadow-sm">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-100">
                  <tr>
                    <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">#</th>
                    <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">Class Title</th>
                    <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">Date & Time</th>
                    <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">Subject</th>
                    <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">Instructor</th>
                    <th scope="col" className="px-4 py-3 text-center text-xs font-medium text-gray-600 uppercase tracking-wider">Status</th>
                    <th scope="col" className="px-4 py-3 text-center text-xs font-medium text-gray-600 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {tableRows}
                </tbody>
              </table>
            </div>
            
            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between px-4 py-3 bg-white border-t border-gray-200 sm:px-6">
                <div className="flex justify-between sm:hidden">
                  <button
                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                    disabled={currentPage === 1}
                    className="relative inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                    disabled={currentPage === totalPages}
                    className="relative ml-3 inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
                  >
                    Next
                  </button>
                </div>
                <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
                  <div>
                    <p className="text-sm text-gray-700">
                      Showing <span className="font-medium">{(currentPage - 1) * ITEMS_PER_PAGE + 1}</span> to{' '}
                      <span className="font-medium">{Math.min(currentPage * ITEMS_PER_PAGE, mergedClasses.length)}</span> of{' '}
                      <span className="font-medium">{mergedClasses.length}</span> classes
                    </p>
                  </div>
                  <div>
                    <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                      <button
                        onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                        disabled={currentPage === 1}
                        className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                      >
                        <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      </button>
                      
                      {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => {
                        if (
                          page === 1 ||
                          page === totalPages ||
                          (page >= currentPage - 2 && page <= currentPage + 2)
                        ) {
                          return (
                            <button
                              key={page}
                              onClick={() => setCurrentPage(page)}
                              className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                                page === currentPage
                                  ? 'z-10 border-indigo-500 text-white'
                                  : 'border-gray-300 text-gray-500 hover:bg-gray-50'
                              }`}
                              style={page === currentPage ? { backgroundColor: currentColor } : {}}
                            >
                              {page}
                            </button>
                          );
                        }
                        return null;
                      })}
                      
                      <button
                        onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                        disabled={currentPage === totalPages}
                        className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                      >
                        <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                        </svg>
                      </button>
                    </nav>
                  </div>
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="bg-white border border-gray-200 rounded-lg p-6 text-center shadow-sm">
            <div className="flex justify-center mb-4">
              <div className="p-4 rounded-full bg-gray-100 text-gray-400">
                <FiVideo size={24} />
              </div>
            </div>
            <p className="text-gray-700 font-medium mb-4">No classes created for this batch yet</p>
            <button
              onClick={() => setShowNewClassForm(true)}
              className="px-4 py-2 rounded-md text-white text-sm font-medium inline-flex items-center gap-2 shadow-sm"
              style={{ backgroundColor: currentColor }}
            >
              <FiPlus size={14} /> Create New Class
            </button>
          </div>
        )}
      </div>

      {/* New Class Form Modal - Simplified */}
      {showNewClassForm && (
        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75" onClick={() => setShowNewClassForm(false)}></div>
            
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-medium text-gray-900">Create New Live Class</h3>
                  <button 
                    type="button" 
                    className="text-gray-400 hover:text-gray-500"
                    onClick={() => setShowNewClassForm(false)}
                  >
                    <svg className="h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
                
                {/* Show error in modal if any */}
                {error && (
                  <div className="mb-4 p-4 bg-red-50 border-l-4 border-red-400 rounded-md">
                    <div className="flex">
                      <div className="ml-3">
                        <h3 className="text-sm font-medium text-red-800">Error</h3>
                        <div className="mt-2 text-sm text-red-700">{error}</div>
                        <button
                          onClick={() => setError(null)}
                          className="mt-2 text-xs text-red-600 hover:text-red-500 underline"
                        >
                          Dismiss
                        </button>
                      </div>
                    </div>
                  </div>
                )}
                
                <form onSubmit={createLiveClass}>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Class Title *</label>
                      <input
                        type="text"
                        name="title"
                        value={newClass.title}
                        onChange={handleFormChange}
                        className="px-3 py-2 border border-gray-300 rounded-md w-full focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="Enter class title"
                        disabled={creatingClass}
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Start Time *</label>
                      <input
                        type="datetime-local"
                        name="start_time"
                        value={newClass.start_time}
                        onChange={handleFormChange}
                        className="px-3 py-2 border border-gray-300 rounded-md w-full focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        disabled={creatingClass}
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Subject *</label>
                      <select
                        name="subject"
                        value={newClass.subject}
                        onChange={handleFormChange}
                        className="px-3 py-2 border border-gray-300 rounded-md w-full focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        disabled={creatingClass}
                      >
                        <option value="">Select a subject</option>
                        <option value="Reasoning">Reasoning</option>
                        <option value="SST">SST</option>
                        <option value="English">English</option>
                        <option value="Maths">Maths</option>
                        <option value="Science">Science</option>
                      </select>
                    </div>
                  </div>
                  
                  <div className="py-3 bg-gray-50 flex justify-end gap-3 mt-4 -mx-6 px-6">
                    <button
                      type="button"
                      className="px-4 py-2 bg-white border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                      onClick={() => setShowNewClassForm(false)}
                      disabled={creatingClass}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="px-4 py-2 rounded-md text-white text-sm font-medium shadow-sm disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                      style={{ backgroundColor: currentColor }}
                      disabled={creatingClass}
                    >
                      {creatingClass ? (
                        <>
                          <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          Creating...
                        </>
                      ) : (
                        'Create & Start Class'
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Success Modal */}
      <SuccessModal />
    </div>
  );
};

export default BatchLive;